import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { IoSearch, IoClose, IoAdd, IoTrashOutline, IoImageOutline } from 'react-icons/io5';
import { PRODUCT_CATEGORIES, ETC_CATEGORY } from '../../../config/appConfig';
import { db, storage } from '../../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../../utils/imageUtils';
import { sortProductsByNewest, getCategoryTabs } from '../../../utils/productUtils';

const categoryKeys = Object.keys(PRODUCT_CATEGORIES);

const INITIAL_FORM = {
  name: '',
  category1: categoryKeys[0],
  category1Custom: '',
  category2: PRODUCT_CATEGORIES[categoryKeys[0]]?.[0] || '',
  category2Custom: '',
  mainImage: null,
  soldOut: false,
  options: [], // [{ name: '', values: [''] }]
};

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('전체');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null=신규, string=수정
  const [form, setForm] = useState(INITIAL_FORM);
  const [mainPreview, setMainPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const mainInputRef = useRef(null);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const snap = await getDocs(collection(db, 'products'));
      setProducts(sortProductsByNewest(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    } catch (err) {
      console.error('상품 로드 실패:', err);
    }
  };

  // 대분류 변경 시 소분류 초기화
  const handleCategory1Change = (val) => {
    const subs = PRODUCT_CATEGORIES[val] || [];
    setForm(prev => ({
      ...prev,
      category1: val,
      category1Custom: '',
      category2: subs.length > 0 ? subs[0] : '',
      category2Custom: '',
    }));
  };

  const handleMainImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setForm(prev => ({ ...prev, mainImage: compressed }));
    setMainPreview(URL.createObjectURL(compressed));
    e.target.value = '';
  };

  // 옵션 그룹 추가 (최대 2개)
  const addOptionGroup = () => {
    if (form.options.length >= 2) return;
    setForm(prev => ({ ...prev, options: [...prev.options, { name: '', values: [{ value: '', soldOut: false }] }] }));
  };

  // 옵션 그룹 삭제
  const removeOptionGroup = (idx) => {
    setForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  // 옵션명 변경
  const updateOptionName = (idx, name) => {
    setForm(prev => {
      const opts = [...prev.options];
      opts[idx] = { ...opts[idx], name };
      return { ...prev, options: opts };
    });
  };

  // 옵션값 변경 (객체 형태: { value, soldOut })
  const updateOptionValue = (gIdx, vIdx, val) => {
    setForm(prev => {
      const opts = [...prev.options];
      const values = [...opts[gIdx].values];
      values[vIdx] = { ...values[vIdx], value: val };
      opts[gIdx] = { ...opts[gIdx], values };
      return { ...prev, options: opts };
    });
  };

  // 옵션값 품절 토글
  const toggleOptionValueSoldOut = (gIdx, vIdx) => {
    setForm(prev => {
      const opts = [...prev.options];
      const values = [...opts[gIdx].values];
      values[vIdx] = { ...values[vIdx], soldOut: !values[vIdx].soldOut };
      opts[gIdx] = { ...opts[gIdx], values };
      return { ...prev, options: opts };
    });
  };

  // 옵션값 추가
  const addOptionValue = (gIdx) => {
    setForm(prev => {
      const opts = [...prev.options];
      opts[gIdx] = { ...opts[gIdx], values: [...opts[gIdx].values, { value: '', soldOut: false }] };
      return { ...prev, options: opts };
    });
  };

  // 옵션값 삭제
  const removeOptionValue = (gIdx, vIdx) => {
    setForm(prev => {
      const opts = [...prev.options];
      const values = opts[gIdx].values.filter((_, i) => i !== vIdx);
      opts[gIdx] = { ...opts[gIdx], values: values.length > 0 ? values : [{ value: '', soldOut: false }] };
      return { ...prev, options: opts };
    });
  };

  // 품절 토글
  const toggleSoldOut = async (e, product) => {
    e.stopPropagation();
    try {
      await updateDoc(doc(db, 'products', product.id), { soldOut: !product.soldOut });
      loadProducts();
    } catch (err) {
      console.error('품절 상태 변경 실패:', err);
    }
  };

  // 상품 카드 클릭 → 수정 폼 열기
  const openEdit = (product) => {
    const cat1 = product.category1 || product.category || categoryKeys[0];
    const cat2 = product.category2 || '';
    // 목록에 없는 분류(직접 입력해서 등록한 값)는 '기타' + 직접입력으로 복원
    const isEtc1 = !categoryKeys.includes(cat1);
    const subs = PRODUCT_CATEGORIES[isEtc1 ? ETC_CATEGORY : cat1] || [];
    const isEtc2 = !!cat2 && !subs.includes(cat2);
    setEditingId(product.id);
    setForm({
      name: product.name || '',
      category1: isEtc1 ? ETC_CATEGORY : cat1,
      category1Custom: isEtc1 ? cat1 : '',
      category2: isEtc2 ? ETC_CATEGORY : cat2,
      category2Custom: isEtc2 ? cat2 : '',
      mainImage: null,
      soldOut: product.soldOut || false,
      options: product.options && product.options.length > 0
        ? product.options.map(o => ({
            name: o.name || '',
            values: o.values?.length > 0
              ? o.values.map(v => typeof v === 'string' ? { value: v, soldOut: false } : { value: v.value || '', soldOut: !!v.soldOut })
              : [{ value: '', soldOut: false }]
          }))
        : [],
    });
    setMainPreview(product.image || null);
    setShowForm(true);
  };

  // 신규 등록 폼 열기
  const openCreate = () => {
    setEditingId(null);
    setForm(INITIAL_FORM);
    setMainPreview(null);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    // 신규는 이미지 필수, 수정은 기존 이미지 있으면 OK
    if (!editingId && !form.mainImage) return;
    setSaving(true);
    try {
      let imageUrl = mainPreview; // 수정 시 기존 이미지 URL 유지

      // 새 이미지를 선택한 경우에만 업로드
      if (form.mainImage) {
        const ts = Date.now();
        const mainRef = ref(storage, `products/${ts}_main`);
        await uploadBytes(mainRef, form.mainImage);
        imageUrl = await getDownloadURL(mainRef);
      }

      // 옵션 정리: 빈 이름/값 제거
      const cleanOptions = form.options
        .filter(o => o.name.trim())
        .map(o => ({
          name: o.name.trim(),
          values: o.values
            .filter(v => (v.value || '').trim())
            .map(v => ({ value: (v.value || '').trim(), soldOut: !!v.soldOut }))
        }))
        .filter(o => o.values.length > 0);

      // '기타' 선택 시 직접 입력한 분류명 사용 (미입력이면 '기타')
      const cat1 = form.category1 === ETC_CATEGORY
        ? (form.category1Custom.trim() || ETC_CATEGORY)
        : form.category1;
      const cat2 = form.category2 === ETC_CATEGORY
        ? (form.category2Custom.trim() || ETC_CATEGORY)
        : form.category2;

      const data = {
        name: form.name.trim(),
        category1: cat1,
        category2: cat2 || '',
        image: imageUrl,
        soldOut: form.soldOut || false,
        options: cleanOptions,
      };

      if (editingId) {
        await updateDoc(doc(db, 'products', editingId), data);
      } else {
        await addDoc(collection(db, 'products'), { ...data, createdAt: serverTimestamp() });
      }

      closeForm();
      loadProducts();
    } catch (err) {
      console.error('상품 저장 실패:', err);
    } finally {
      setSaving(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(INITIAL_FORM);
    setMainPreview(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('상품을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      closeForm();
      loadProducts();
    } catch (err) {
      console.error('상품 삭제 실패:', err);
    }
  };

  const filtered = products.filter(p => {
    if (category !== '전체') {
      // 레거시 category 필드 또는 새 category1 필드로 필터
      const cat = p.category1 || p.category;
      if (cat !== category) return false;
    }
    if (search && !p.name.includes(search)) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const subCategories = PRODUCT_CATEGORIES[form.category1] || [];
  const categoryTabs = getCategoryTabs(products);

  return (
    <Container>
      <PageHeader>
        <HeaderRight>
          <SearchBox>
            <IoSearch size={18} color="#8C9AA4" />
            <SearchInput placeholder="상품명 검색" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </SearchBox>
          <AddBtn onClick={openCreate}><IoAdd size={18} /> 상품 등록</AddBtn>
        </HeaderRight>
      </PageHeader>

      <TabRow>
        <Tab $active={category === '전체'} onClick={() => { setCategory('전체'); setPage(1); }}>전체</Tab>
        {categoryTabs.map(c => (
          <Tab key={c} $active={category === c} onClick={() => { setCategory(c); setPage(1); }}>{c}</Tab>
        ))}
      </TabRow>

      <Grid>
        {paged.map(p => (
          <Card key={p.id} onClick={() => openEdit(p)} $soldOut={p.soldOut}>
            <CardImgWrap>
              <CardImg src={p.image} alt={p.name} onLoad={e => e.target.style.opacity = 1} />
              {p.soldOut && <SoldOutOverlay>품절</SoldOutOverlay>}
            </CardImgWrap>
            <CardBody>
              <CardCategory>{p.category1 || p.category}{p.category2 ? ` > ${p.category2}` : ''}</CardCategory>
              <CardName>{p.name}</CardName>
              <SoldOutToggle $soldOut={p.soldOut} onClick={(e) => toggleSoldOut(e, p)}>
                {p.soldOut ? '품절' : '판매중'}
              </SoldOutToggle>
            </CardBody>
          </Card>
        ))}
      </Grid>

      {filtered.length === 0 && <Empty>등록된 상품이 없습니다</Empty>}

      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page === 1} onClick={() => setPage(1)}>«</PageBtn>
          <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((p, i) => p === '...' ? <PageDots key={`dots-${i}`}>…</PageDots> : <PageNum key={p} $active={page === p} onClick={() => setPage(p)}>{p}</PageNum>)}
          <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</PageBtn>
          <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
          <PageInfo>{filtered.length}건 중 {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)}</PageInfo>
        </Pagination>
      )}

      {/* 상품 등록/수정 폼 */}
      {showForm && (
        <Overlay onClick={closeForm}>
          <FormModal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingId ? '상품 수정' : '상품 등록'}</ModalTitle>
              <ModalActions>
                {editingId && <DeleteBtn onClick={() => handleDelete(editingId)}><IoTrashOutline size={18} /></DeleteBtn>}
                <CloseBtn onClick={closeForm}><IoClose size={22} /></CloseBtn>
              </ModalActions>
            </ModalHeader>
            <ModalBody>
              {/* 대분류 */}
              <FormLabel>대분류</FormLabel>
              <FormSelect value={form.category1} onChange={e => handleCategory1Change(e.target.value)}>
                {categoryKeys.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </FormSelect>

              {form.category1 === ETC_CATEGORY && (
                <CategoryCustomInput
                  placeholder="대분류 직접 입력 (미입력 시 '기타'로 등록)"
                  value={form.category1Custom}
                  onChange={e => setForm({ ...form, category1Custom: e.target.value })}
                />
              )}

              {/* 소분류 */}
              <FormLabel>소분류</FormLabel>
              <FormSelect value={form.category2} onChange={e => setForm({ ...form, category2: e.target.value })}>
                {subCategories.length === 0 && <option value="">선택 안 함</option>}
                {subCategories.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
                <option value={ETC_CATEGORY}>{ETC_CATEGORY}</option>
              </FormSelect>
              {form.category2 === ETC_CATEGORY && (
                <CategoryCustomInput
                  placeholder="소분류 직접 입력 (미입력 시 '기타'로 등록)"
                  value={form.category2Custom}
                  onChange={e => setForm({ ...form, category2Custom: e.target.value })}
                />
              )}

              {/* 상품명 */}
              <FormLabel>상품명</FormLabel>
              <FormInput placeholder="상품명을 입력하세요" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />

              {/* 메인 사진 */}
              <FormLabel>메인 사진</FormLabel>
              {mainPreview ? (
                <MainPreviewWrap>
                  <MainPreviewImg src={mainPreview} alt="메인" />
                  <PreviewRemove onClick={() => { setForm(prev => ({ ...prev, mainImage: null })); setMainPreview(null); }}><IoClose size={16} /></PreviewRemove>
                </MainPreviewWrap>
              ) : (
                <UploadArea onClick={() => mainInputRef.current?.click()}>
                  <IoImageOutline size={24} color="#8C9AA4" />
                  <UploadText>사진 선택</UploadText>
                  <UploadHint>350x350 자동 변환</UploadHint>
                </UploadArea>
              )}
              <input ref={mainInputRef} type="file" accept="image/*" hidden onChange={handleMainImage} />

              {/* 품절 설정 */}
              <SoldOutCheckRow onClick={() => setForm(prev => ({ ...prev, soldOut: !prev.soldOut }))}>
                <SoldOutCheckBox $checked={form.soldOut} />
                <SoldOutCheckLabel>품절 처리</SoldOutCheckLabel>
              </SoldOutCheckRow>

              {/* 옵션 섹션 */}
              <FormDivider />
              <OptionHeader>
                <FormSectionLabel>옵션</FormSectionLabel>
                {form.options.length < 2 && (
                  <OptionAddBtn onClick={addOptionGroup}><IoAdd size={14} /> 옵션 추가</OptionAddBtn>
                )}
              </OptionHeader>
              {form.options.length === 0 && <OptionHint>옵션이 없습니다. 필요 시 추가하세요.</OptionHint>}

              {form.options.map((opt, gIdx) => (
                <OptionGroup key={gIdx}>
                  <OptionGroupHeader>
                    <OptionNameInput
                      placeholder="옵션명 (예: 중량, 색상)"
                      value={opt.name}
                      onChange={e => updateOptionName(gIdx, e.target.value)}
                    />
                    <OptionGroupRemove onClick={() => removeOptionGroup(gIdx)}><IoClose size={16} /></OptionGroupRemove>
                  </OptionGroupHeader>
                  <OptionValues>
                    {opt.values.map((v, vIdx) => (
                      <OptionValueRow key={vIdx}>
                        <OptionValueInput
                          placeholder={`옵션값 ${vIdx + 1}`}
                          value={v.value || ''}
                          onChange={e => updateOptionValue(gIdx, vIdx, e.target.value)}
                        />
                        <OptionSoldOutBtn $soldOut={v.soldOut} onClick={() => toggleOptionValueSoldOut(gIdx, vIdx)}>
                          {v.soldOut ? '품절' : '판매중'}
                        </OptionSoldOutBtn>
                        {opt.values.length > 1 && (
                          <OptionValueRemove onClick={() => removeOptionValue(gIdx, vIdx)}><IoClose size={14} /></OptionValueRemove>
                        )}
                      </OptionValueRow>
                    ))}
                    <OptionValueAddBtn onClick={() => addOptionValue(gIdx)}>+ 값 추가</OptionValueAddBtn>
                  </OptionValues>
                </OptionGroup>
              ))}

              <SubmitBtn onClick={handleSubmit} disabled={saving || !form.name.trim() || (!editingId && !form.mainImage)}>
                {saving ? '저장 중...' : editingId ? '수정 완료' : '상품 등록'}
              </SubmitBtn>
            </ModalBody>
          </FormModal>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminProductsPage;

const Pagination = styled.div`display: flex; align-items: center; justify-content: center; gap: 4px; padding: 20px 0 8px;`;
const PageBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 14px;
  background: #fff; color: #666; border: none;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:disabled { opacity: 0.3; cursor: default; }
  &:hover:not(:disabled) { background: #F0F0EC; }
`;
const PageNum = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 13px; font-weight: 500;
  border: none; display: flex; align-items: center; justify-content: center;
  background: ${p => p.$active ? '#4A6741' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
  &:hover { background: ${p => p.$active ? '#3B5534' : '#F0F0EC'}; }
`;
const PageDots = styled.span`font-size: 13px; color: #8C9AA4; padding: 0 4px;`;
const PageInfo = styled.span`font-size: 12px; color: #8C9AA4; margin-left: 12px;`;
const Container = styled.div`padding: 32px;`;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`;
const HeaderRight = styled.div`display: flex; gap: 12px; align-items: center;`;
const SearchBox = styled.div`display: flex; align-items: center; gap: 8px; background: #fff; border: none; border-radius: 10px; padding: 8px 14px; width: 260px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);`;
const SearchInput = styled.input`border: none; outline: none; font-size: 13px; flex: 1; background: transparent;`;
const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 10px;
  background: #fff; color: #1E2A32; font-size: 13px; font-weight: 600;
  border: 1.5px solid #D0D0D0;
  &:hover { background: #F5F5F5; border-color: #999; }
`;

const TabRow = styled.div`display: flex; gap: 8px; margin-bottom: 24px; flex-wrap: wrap;`;
const Tab = styled.button`
  padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 400;
  white-space: nowrap;
  background: ${p => p.$active ? '#8C9AA4' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: none;
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
`;

const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px;`;
const Card = styled.div`
  background: #fff; border-radius: 10px; overflow: hidden; cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: transform 0.15s;
  opacity: ${p => p.$soldOut ? 0.6 : 1};
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
`;
const CardImgWrap = styled.div`
  width: 100%; aspect-ratio: 1; position: relative; background: #ECEEE9; overflow: hidden;
  &::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%);
    animation: shimmer 1.5s infinite;
  }
  @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
`;
const CardImg = styled.img`width: 100%; height: 100%; object-fit: contain; position: relative; z-index: 1; background: #F5F5F0; opacity: 0; transition: opacity 0.4s ease;`;
const CardBody = styled.div`padding: 10px 12px 12px;`;
const CardCategory = styled.div`font-size: 10px; color: #4A6741; font-weight: 400; margin-bottom: 2px;`;
const CardName = styled.div`font-size: 13px; font-weight: 500; color: #1E2A32; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;

const SoldOutOverlay = styled.div`
  position: absolute; inset: 0; z-index: 2;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 700; color: #fff; letter-spacing: 2px;
`;

const SoldOutToggle = styled.button`
  margin-top: 6px; font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 4px;
  color: ${p => p.$soldOut ? '#D32F2F' : '#4A6741'};
  background: ${p => p.$soldOut ? '#FFEBEE' : '#E8F5E9'};
  &:hover { opacity: 0.8; }
`;

const SoldOutCheckRow = styled.div`
  display: flex; align-items: center; gap: 10px; margin-top: 20px; cursor: pointer;
  padding: 12px 14px; border-radius: 8px; background: #F9F9F7;
`;

const SoldOutCheckBox = styled.div`
  width: 20px; height: 20px; border-radius: 4px;
  border: 2px solid ${p => p.$checked ? '#D32F2F' : '#CCC'};
  background: ${p => p.$checked ? '#D32F2F' : '#fff'};
  display: flex; align-items: center; justify-content: center;
  &::after {
    content: '${p => p.$checked ? '✓' : ''}';
    color: #fff; font-size: 12px; font-weight: 700;
  }
`;

const SoldOutCheckLabel = styled.span`font-size: 14px; color: #1E2A32; font-weight: 500;`;

const Empty = styled.div`text-align: center; padding: 48px; color: #8C9AA4; font-size: 14px;`;

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const FormModal = styled.div`background: #fff; border-radius: 16px; width: 440px; max-height: 90vh; overflow-y: auto;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #F0F0EC; position: sticky; top: 0; background: #fff; z-index: 1; border-radius: 16px 16px 0 0;`;
const ModalTitle = styled.h3`font-size: 16px; font-weight: 500;`;
const ModalActions = styled.div`display: flex; align-items: center; gap: 8px;`;
const CloseBtn = styled.button`color: #8C9AA4; display: flex;`;
const DeleteBtn = styled.button`color: #EF5350; display: flex; &:hover { color: #D32F2F; }`;
const ModalBody = styled.div`padding: 20px;`;

const FormLabel = styled.label`display: block; font-size: 13px; color: #666; margin-bottom: 6px; margin-top: 16px; &:first-child { margin-top: 0; }`;
const FormInput = styled.input`width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 13px; &:focus { border-color: #999; outline: none; }`;
const CategoryCustomInput = styled.input`
  width: 100%; margin-top: 8px; padding: 10px 12px;
  border: 1px solid #ddd; border-radius: 8px; font-size: 13px;
  &:focus { border-color: #999; outline: none; }
`;
const FormSelect = styled.select`width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 13px; background: #fff; &:focus { border-color: #999; outline: none; }`;

const FormDivider = styled.div`height: 1px; background: #EEEEF0; margin: 20px 0 4px;`;
const FormSectionLabel = styled.div`font-size: 14px; font-weight: 500; color: #1E2A32; display: flex; align-items: center; gap: 6px;`;

const MainPreviewWrap = styled.div`position: relative; width: 120px; height: 120px; border-radius: 8px; overflow: hidden;`;
const MainPreviewImg = styled.img`width: 100%; height: 100%; object-fit: cover; border-radius: 8px; background: #F5F5F0;`;

const UploadArea = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  width: 120px; height: 120px; border: 2px dashed #ddd; border-radius: 8px; cursor: pointer;
  &:hover { border-color: #4A6741; background: #FAFFF9; }
`;
const UploadText = styled.span`font-size: 13px; color: #666; margin-top: 6px;`;
const UploadHint = styled.span`font-size: 11px; color: #999; margin-top: 2px;`;

const PreviewRemove = styled.button`
  position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%;
  background: rgba(0,0,0,0.5); color: #fff; display: flex; align-items: center; justify-content: center;
  &:hover { background: rgba(0,0,0,0.7); }
`;

const SubmitBtn = styled.button`
  width: 100%; padding: 14px; margin-top: 24px; border-radius: 10px;
  background: #4A6741; color: white; font-size: 15px; font-weight: 500;
  &:disabled { opacity: 0.5; }
  &:hover:not(:disabled) { background: #3B5534; }
`;

/* 옵션 UI */
const OptionHeader = styled.div`display: flex; align-items: center; justify-content: space-between; margin-top: 12px; margin-bottom: 8px;`;
const OptionAddBtn = styled.button`
  display: flex; align-items: center; gap: 4px; font-size: 12px; color: #4A6741; font-weight: 500;
  &:hover { text-decoration: underline; }
`;
const OptionHint = styled.div`font-size: 12px; color: #8C9AA4; margin-bottom: 4px;`;
const OptionGroup = styled.div`background: #F9F9F7; border-radius: 10px; padding: 14px; margin-bottom: 10px;`;
const OptionGroupHeader = styled.div`display: flex; align-items: center; gap: 8px; margin-bottom: 10px;`;
const OptionNameInput = styled.input`
  flex: 1; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 13px; font-weight: 500;
  &:focus { border-color: #999; outline: none; }
`;
const OptionGroupRemove = styled.button`color: #999; display: flex; &:hover { color: #EF5350; }`;
const OptionValues = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const OptionValueRow = styled.div`display: flex; align-items: center; gap: 6px;`;
const OptionValueInput = styled.input`
  flex: 1; padding: 7px 10px; border: 1px solid #e0e0e0; border-radius: 6px; font-size: 13px; background: #fff;
  &:focus { border-color: #999; outline: none; }
`;
const OptionSoldOutBtn = styled.button`
  font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 4px; white-space: nowrap; flex-shrink: 0;
  color: ${p => p.$soldOut ? '#D32F2F' : '#4A6741'};
  background: ${p => p.$soldOut ? '#FFEBEE' : '#E8F5E9'};
  &:hover { opacity: 0.8; }
`;
const OptionValueRemove = styled.button`color: #ccc; display: flex; &:hover { color: #EF5350; }`;
const OptionValueAddBtn = styled.button`
  font-size: 12px; color: #4A6741; text-align: left; padding: 4px 0; margin-top: 2px;
  &:hover { text-decoration: underline; }
`;
